import { ModbusRTU } from '../modbus/ModbusRTU';
import { FrameAssembler, type FrameCriteria } from '../modbus/FrameAssembler';
import { Terminal } from '../ui/Terminal';
import { StateManager } from '../core/StateManager';

export class SerialManager {
  private port: globalThis.SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private reading = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private assembler = new FrameAssembler();

  private responseResolve: ((value: Uint8Array | null) => void) | null = null;
  private responseTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingCriteria: FrameCriteria | null = null;

  private writeChain: Promise<void> = Promise.resolve();
  private userDisconnected = false;

  private readonly RECONNECT_DELAY = 3000;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private reconnectAttempts = 0;

  constructor(
    private state: StateManager,
    private terminal: Terminal,
  ) {}

  async connect(): Promise<void> {
    const nav = navigator as unknown as {
      serial: { requestPort(): Promise<globalThis.SerialPort> };
    };
    if (!nav.serial) {
      throw new Error(
        'Web Serial API não é suportada. Use Chrome ou Edge atualizados.',
      );
    }

    try {
      this.userDisconnected = false;
      this.port = await nav.serial.requestPort();
      await this.port.open({
        baudRate: 9600,
        parity: 'none',
        stopBits: 1,
        dataBits: 8,
      });

      this.state.setConnectionMode('serial');
      this.reconnectAttempts = 0;
      this.terminal.log(
        'COM Serial Conectada com Sucesso [9600 Baud, 8N1]',
        'system',
      );

      this.startReading();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Erro desconhecido';
      this.terminal.log(`Falha ao conectar Serial: ${message}`, 'error');
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    this.userDisconnected = true;
    this.reading = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    await this.cleanupPort();

    this.state.setConnectionMode('sim');
    this.reconnectAttempts = 0;
    this.assembler.reset();
    this.rejectPendingResponse();
    this.terminal.log('Conexão Serial Encerrada', 'system');
  }

  async sendFrame(buffer: Uint8Array): Promise<void> {
    const serialPort = this.port;
    if (!serialPort?.writable) {
      throw new Error('Porta serial não está conectada');
    }

    const writable = serialPort.writable;

    try {
      await this.withWriteLock(async () => {
        this.writer = writable.getWriter();
        try {
          await this.writer.write(buffer);
        } finally {
          this.writer.releaseLock();
          this.writer = null;
        }
      });
      this.terminal.log(
        `TX REAL -> ${ModbusRTU.bytesToHex(buffer)}`,
        'tx',
      );
    } catch (err) {
      this.terminal.log(
        `Erro ao escrever na serial: ${err instanceof Error ? err.message : 'desconhecido'}`,
        'error',
      );
      this.handleDisconnect();
      throw err;
    }
  }

  /**
   * Transação completa: envia o frame e aguarda o frame de resposta que
   * corresponde ao slaveId/functionCode enviado (validação de CRC inclusa).
   */
  async transact(
    frame: Uint8Array,
    timeoutMs = 1000,
  ): Promise<Uint8Array> {
    await this.sendFrame(frame);
    this.pendingCriteria = {
      slaveId: frame[0],
      functionCode: frame[1],
    };
    const response = await this.waitForResponse(timeoutMs);
    if (!response) {
      throw new Error(`Timeout aguardando resposta serial (${timeoutMs}ms)`);
    }
    return response;
  }

  async waitForResponse(
    timeoutMs = 1000,
  ): Promise<Uint8Array | null> {
    if (this.responseResolve) return null;

    return new Promise<Uint8Array | null>((resolve) => {
      this.responseResolve = resolve;
      this.responseTimer = setTimeout(() => {
        this.responseResolve = null;
        this.pendingCriteria = null;
        this.responseTimer = null;
        resolve(null);
      }, timeoutMs);
    });
  }

  get isConnected(): boolean {
    return (
      this.port !== null &&
      this.port.readable !== null &&
      this.port.readable !== undefined
    );
  }

  private async startReading(): Promise<void> {
    this.reading = true;

    while (this.reading && this.port?.readable) {
      try {
        this.reader = this.port.readable.getReader();

        while (true) {
          const { value, done } = await this.reader.read();
          if (done) break;

          if (value && value.length > 0) {
            this.terminal.log(
              `RX REAL <- ${ModbusRTU.bytesToHex(value)}`,
              'rx',
            );
            this.assembler.push(value);
            this.dispatchFrames();
          }
        }
      } catch (err) {
        if (this.reading) {
          this.terminal.log(
            `Erro de Leitura Serial: ${err instanceof Error ? err.message : 'desconhecido'}`,
            'error',
          );
          this.handleDisconnect();
        }
      } finally {
        await this.closeReader();
      }
    }
  }

  private dispatchFrames(): void {
    while (this.responseResolve) {
      const frame = this.assembler.next(this.pendingCriteria ?? undefined);
      if (!frame) break;

      this.terminal.log(
        `RX FRAME <- ${ModbusRTU.bytesToHex(frame)}`,
        'rx',
      );

      const resolve = this.responseResolve;
      this.responseResolve = null;
      this.pendingCriteria = null;
      if (this.responseTimer) {
        clearTimeout(this.responseTimer);
        this.responseTimer = null;
      }
      resolve(frame);
    }
  }

  private rejectPendingResponse(): void {
    if (this.responseTimer) {
      clearTimeout(this.responseTimer);
      this.responseTimer = null;
    }
    if (this.responseResolve) {
      const resolve = this.responseResolve;
      this.responseResolve = null;
      this.pendingCriteria = null;
      resolve(null);
    }
  }

  private handleDisconnect(): void {
    if (!this.reading) return;

    this.reading = false;
    this.state.setConnectionMode('sim');

    if (this.userDisconnected) return;

    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      this.terminal.log(
        'Número máximo de tentativas de reconexão atingido.',
        'error',
      );
      return;
    }

    this.reconnectAttempts++;
    this.terminal.log(
      `Tentando reconexão (${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS}) em ${this.RECONNECT_DELAY}ms...`,
      'warn',
    );

    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.attemptReconnect();
    }, this.RECONNECT_DELAY);
  }

  private async attemptReconnect(): Promise<void> {
    await this.cleanupPort();
    try {
      await this.connect();
      this.terminal.log('Reconexão estabelecida com sucesso', 'system');
    } catch {
      this.terminal.log('Falha na reconexão', 'error');
      if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
        this.terminal.log(
          'Número máximo de tentativas de reconexão atingido.',
          'error',
        );
        return;
      }
      this.reconnectAttempts++;
      this.terminal.log(
        `Tentando reconexão (${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS}) em ${this.RECONNECT_DELAY}ms...`,
        'warn',
      );
      this.scheduleReconnect();
    }
  }

  private async cleanupPort(): Promise<void> {
    await this.closeReader();
    await this.closeWriter();

    if (this.port) {
      try {
        await this.port.close();
      } catch {
        // porta já pode estar fechada
      }
      this.port = null;
    }
  }

  private async withWriteLock<T>(fn: () => Promise<T>): Promise<T> {
    const prev = this.writeChain;
    let release!: () => void;
    this.writeChain = new Promise<void>((r) => {
      release = r;
    });
    await prev;
    try {
      return await fn();
    } finally {
      release();
    }
  }

  private async closeReader(): Promise<void> {
    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch {
        // ignore
      }
      this.reader.releaseLock();
      this.reader = null;
    }
  }

  private async closeWriter(): Promise<void> {
    if (this.writer) {
      try {
        await this.writer.close();
      } catch {
        // ignore
      }
      this.writer.releaseLock();
      this.writer = null;
    }
  }
}
